import { FreightChargeType, type OrderLineUpgradeData, PricingMode } from "~/gql/graphqlBase.generated";

export const calcTotalEaches = (eachesQuantity?: number | null, handlingUom?: number | null): number =>
	(eachesQuantity || 0) * (handlingUom || 1);

export const hasQuantityPairDrift = (
	eachesQuantity: number | null | undefined,
	handlingUom: number | null | undefined,
	orderedQuantity: number | null | undefined
): boolean => orderedQuantity != null && calcTotalEaches(eachesQuantity, handlingUom) !== orderedQuantity;

export const coalesceAcknowledgedQuantity = (
	acknowledgedQuantity?: number | null,
	orderedQuantity?: number | null
): number | null => acknowledgedQuantity ?? orderedQuantity ?? null;

export const coalesceShippedQuantity = (
	shippedQuantity?: number | null,
	acknowledgedQuantity?: number | null,
	orderedQuantity?: number | null
): number | null => shippedQuantity ?? acknowledgedQuantity ?? orderedQuantity ?? null;

export interface BillingQuantityInputs {
	acknowledgedQuantity?: number | null;
	acknowledgedZeroBillsNothing?: boolean | null;
	eachesQuantity?: number | null;
	orderedQuantity?: number | null;
	shippedQuantity?: number | null;
	shippedZeroBillsNothing?: boolean | null;
}

const pricesAtShippedZero = ({ shippedQuantity, shippedZeroBillsNothing }: BillingQuantityInputs): boolean =>
	Boolean(shippedZeroBillsNothing) && shippedQuantity === 0;

const hasPositiveShippedQuantity = ({ shippedQuantity }: BillingQuantityInputs): boolean => (shippedQuantity ?? 0) > 0;

const pricesAtAcknowledgedZero = ({
	acknowledgedQuantity,
	acknowledgedZeroBillsNothing
}: BillingQuantityInputs): boolean => Boolean(acknowledgedZeroBillsNothing) && acknowledgedQuantity === 0;

export type ZeroBillingRung = "acknowledged" | "shipped";

export const resolveZeroBillingRung = (inputs: BillingQuantityInputs): null | ZeroBillingRung => {
	if (pricesAtShippedZero(inputs)) return "shipped";
	if (hasPositiveShippedQuantity(inputs)) return null;
	return pricesAtAcknowledgedZero(inputs) ? "acknowledged" : null;
};

export const resolveBillingQuantity = (inputs: BillingQuantityInputs): number | null =>
	resolveZeroBillingRung(inputs) ? 0 : (inputs.eachesQuantity ?? null);

export interface LineMoneyInputs extends BillingQuantityInputs {
	handlingUom?: number | null;
	overridePrice?: number | null;
	price?: number | null;
}

export const calcStagedLineTotal = (inputs: LineMoneyInputs): number =>
	calcTotalEaches(resolveBillingQuantity(inputs), inputs.handlingUom) * (inputs.overridePrice ?? inputs.price ?? 0);

export interface LineUpgradeMoneyInputs extends LineMoneyInputs {
	upgrades?: readonly Pick<OrderLineUpgradeData, "pricing">[] | null;
}

// Mirrors backend OrderState.CalculateUpgradeAdjustment so the client total matches the stored total.
export const calcLineUpgradeAdjustment = (inputs: LineUpgradeMoneyInputs): number => {
	const qtyBasis = calcTotalEaches(resolveBillingQuantity(inputs), inputs.handlingUom);
	const lineBase = qtyBasis * (inputs.overridePrice ?? inputs.price ?? 0);
	return (inputs.upgrades ?? []).reduce((sum, upgrade) => {
		if (upgrade.pricing.mode === PricingMode.FlatPrice) {
			return sum + (upgrade.pricing.flatPrice ?? 0) * qtyBasis;
		}
		if (upgrade.pricing.mode === PricingMode.MarkupPercent) {
			return sum + ((upgrade.pricing.markupPercent ?? 0) / 100) * lineBase;
		}
		return sum;
	}, 0);
};

export const calcLineTotalWithUpgrades = (inputs: LineUpgradeMoneyInputs): number =>
	calcStagedLineTotal(inputs) + calcLineUpgradeAdjustment(inputs);

const INT_TO_FREIGHT_TYPE: Record<number, FreightChargeType> = {
	0: FreightChargeType.None,
	1: FreightChargeType.Percentage,
	2: FreightChargeType.FlatFee
};

export const resolveFreightChargeType = (raw?: FreightChargeType | number | null): FreightChargeType | null => {
	if (raw == null) return null;
	if (typeof raw === "number") return INT_TO_FREIGHT_TYPE[raw] ?? null;
	return raw;
};
